import { useCallback, useEffect, useRef, useState } from "react";
import { FindBar, type SearchOptions } from "../_findBar";
import { highlightFullText } from "../../../../utils/_getHighlightedExcerpts";
import "./index.scss";

const GlobalFindBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [currentResult, setCurrentResult] = useState(0);

  const matchesRef = useRef<Element[]>([]);
  const currentIndexRef = useRef(0);
  const highlightClass = "pdl-findbar-highlight";
  const currentHighlightClass = "pdl-findbar-highlight-current";

  const originalStateRef = useRef<
    Map<
      Node,
      {
        originalHTML: string;
        parent: Node;
        nextSibling: Node | null;
      }
    >
  >(new Map());

  const saveOriginalState = useCallback(() => {
    originalStateRef.current.clear();

    const searchableElements = document.querySelectorAll(
      "main, article, section, div, p, span, h1, h2, h3, h4, h5, h6, li, td, th, label, .pdl-findbar-searchable"
    );

    const elementsToSave = new Set<HTMLElement>();

    searchableElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        const walker = document.createTreeWalker(
          element,
          NodeFilter.SHOW_TEXT,
          {
            acceptNode: (node) => {
              let parent = node.parentNode;
              while (parent && parent !== element) {
                if (
                  parent instanceof HTMLElement &&
                  (parent.classList.contains(highlightClass) ||
                    parent.classList.contains(currentHighlightClass))
                ) {
                  return NodeFilter.FILTER_REJECT;
                }
                parent = parent.parentNode;
              }
              return NodeFilter.FILTER_ACCEPT;
            },
          }
        );

        let node;
        while ((node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE && node.parentNode) {
            const parent = node.parentNode as HTMLElement;
            if (parent && parent !== element) {
              elementsToSave.add(parent);
            } else if (element instanceof HTMLElement) {
              elementsToSave.add(element);
            }
          }
        }
      }
    });

    elementsToSave.forEach((element) => {
      if (!originalStateRef.current.has(element)) {
        originalStateRef.current.set(element, {
          originalHTML: element.innerHTML,
          parent: element.parentNode!,
          nextSibling: element.nextSibling,
        });
      }
    });
  }, [highlightClass, currentHighlightClass]);

  const hideSearchHighlights = useCallback(() => {
    const searchHighlights = document.querySelectorAll(
      'mark.search-highlight, mark[style*="background-color:#f0eee6"], mark[style*="background-color: #f0eee6"]'
    );
    searchHighlights.forEach((el) => {
      if (
        el instanceof HTMLElement &&
        !el.classList.contains(highlightClass) &&
        !el.classList.contains(currentHighlightClass)
      ) {
        const parent = el.parentNode;
        if (parent) {
          const textNode = document.createTextNode(el.textContent || "");
          parent.replaceChild(textNode, el);
          parent.normalize();
        }
      }
    });
  }, [highlightClass, currentHighlightClass]);

  const restoreOriginalState = useCallback(() => {
    originalStateRef.current.forEach((state, element) => {
      if (element instanceof HTMLElement && state.parent) {
        try {
          element.innerHTML = state.originalHTML;
        } catch {}
      }
    });

    originalStateRef.current.clear();
  }, []);

  const removeFindBarHighlights = useCallback(() => {
    const findBarHighlights = document.querySelectorAll(
      `mark.${highlightClass}, mark.${currentHighlightClass}, mark[class*="${highlightClass}"]`
    );

    findBarHighlights.forEach((el) => {
      if (
        el instanceof HTMLElement &&
        (el.classList.contains(highlightClass) ||
          el.classList.contains(currentHighlightClass) ||
          (el.style.cssText.includes("background-color:#f0eee6") &&
            el.classList.contains(highlightClass)))
      ) {
        const parent = el.parentNode;
        if (parent) {
          const textNode = document.createTextNode(el.textContent || "");
          parent.replaceChild(textNode, el);
          parent.normalize();
        }
      }
    });

    matchesRef.current = [];
  }, [highlightClass, currentHighlightClass]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTotalResults(0);
    setCurrentResult(0);
    currentIndexRef.current = 0;

    restoreOriginalState();
  }, [restoreOriginalState]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        saveOriginalState();
        removeFindBarHighlights();
        hideSearchHighlights();
        setIsOpen(true);
      }

      if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        handleClose();
      }
    };

    const handleOpenFindBar = () => {
      saveOriginalState();
      removeFindBarHighlights();
      hideSearchHighlights();
      setIsOpen(true);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pdl:openFindBar", handleOpenFindBar);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pdl:openFindBar", handleOpenFindBar);
    };
  }, [
    isOpen,
    handleClose,
    saveOriginalState,
    removeFindBarHighlights,
    hideSearchHighlights,
  ]);

  const highlightCurrentMatch = useCallback(
    (index: number) => {
      matchesRef.current.forEach((match) => {
        match.classList.remove(currentHighlightClass);
      });

      if (matchesRef.current[index]) {
        matchesRef.current[index].classList.add(currentHighlightClass);

        matchesRef.current[index].scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    },
    [currentHighlightClass]
  );

  const searchInPage = useCallback(
    (term: string, options: SearchOptions): number => {
      removeFindBarHighlights();

      if (!term.trim()) {
        setTotalResults(0);
        setCurrentResult(0);
        return 0;
      }

      const normalizedTerm = term.toLowerCase().trim();
      const markCache = new WeakSet<HTMLElement>();
      const processedNodes = new WeakSet<Node>();
      const textNodes: Node[] = [];

      const isInsideMark = (node: Node): boolean => {
        let parent = node.parentNode;
        while (parent) {
          if (parent instanceof HTMLElement && parent.tagName === "MARK") {
            if (markCache.has(parent)) return true;
            if (
              parent.classList.contains(highlightClass) ||
              parent.classList.contains(currentHighlightClass) ||
              parent.classList.contains("search-highlight")
            ) {
              markCache.add(parent);
              return true;
            }
          }
          parent = parent.parentNode;
        }
        return false;
      };

      const mainContent = document.querySelector("main") || document.body;
      const walker = document.createTreeWalker(
        mainContent,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (isInsideMark(node)) {
              return NodeFilter.FILTER_REJECT;
            }
            const text = node.textContent || "";
            return text.trim()
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        }
      );

      let node;
      while ((node = walker.nextNode())) {
        if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent?.toLowerCase().includes(normalizedTerm)
        ) {
          textNodes.push(node);
        }
      }

      for (let i = textNodes.length - 1; i >= 0; i--) {
        const textNode = textNodes[i];
        if (!textNode || processedNodes.has(textNode) || !textNode.parentNode) {
          continue;
        }

        const text = textNode.textContent || "";
        const highlightedHTML = highlightFullText(text, term);

        if (highlightedHTML === text) {
          continue;
        }

        try {
          const temp = document.createElement("div");
          temp.innerHTML = highlightedHTML;

          temp.querySelectorAll("mark").forEach((mark) => {
            mark.classList.add(highlightClass);
          });

          if (temp.childNodes.length > 0 && textNode.parentNode) {
            const fragment = document.createDocumentFragment();
            while (temp.firstChild) {
              fragment.appendChild(temp.firstChild);
            }
            textNode.parentNode.replaceChild(fragment, textNode);
            processedNodes.add(textNode);
          }
        } catch {}
      }

      const allMarks = Array.from(
        document.querySelectorAll(`mark.${highlightClass}`)
      ).sort((a, b) => {
        const position = a.compareDocumentPosition(b);
        return position & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
      });

      matchesRef.current = allMarks;
      const count = allMarks.length;
      setTotalResults(count);

      if (count > 0) {
        setCurrentResult(1);
        currentIndexRef.current = 0;
        highlightCurrentMatch(0);
      } else {
        setCurrentResult(0);
        currentIndexRef.current = -1;
      }

      return count;
    },
    [removeFindBarHighlights, highlightClass, highlightCurrentMatch]
  );

  const handleSearch = useCallback(
    (term: string, options: SearchOptions) => {
      searchInPage(term, options);
    },
    [searchInPage]
  );

  const handleNavigate = useCallback(
    (direction: "next" | "previous") => {
      if (totalResults === 0) return;

      let newIndex = currentIndexRef.current;

      if (direction === "next") {
        newIndex = (newIndex + 1) % totalResults;
      } else {
        newIndex = newIndex === 0 ? totalResults - 1 : newIndex - 1;
      }

      currentIndexRef.current = newIndex;
      setCurrentResult(newIndex + 1);
      highlightCurrentMatch(newIndex);
    },
    [totalResults, highlightCurrentMatch]
  );

  return (
    <FindBar
      isOpen={isOpen}
      onClose={handleClose}
      onSearch={handleSearch}
      onNavigate={handleNavigate}
      totalResults={totalResults}
      currentResult={currentResult}
      placeholder="Buscar"
    />
  );
};

export default GlobalFindBar;
